import './Home.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { Button, Col, Row, ToggleButton } from 'react-bootstrap';
import { useGetEvents } from '../../Services/Events/requests';
import moment from 'moment';
import Loader from '../../Components/Loader/Loader';
import EventCalendar from '../../Components/EventCalendar/EventCalendar';
import { CalendarEvent } from '../../Models/calendarEvent';
import { CalendarType } from '../../Constants/calendartype';
import { EventEntryMethods, EventStatuses, EventType, EventTypes } from '../../Constants/eventtype';
import { MdDensityMedium, MdFormatAlignJustify } from 'react-icons/md';
import FilterMenu from '../../Components/FilterMenu/FilterMenu';
import FilterChip from '../../Components/FilterChip/FilterChip';
import { IoMdClose } from 'react-icons/io';

export interface HomeProps {}

const Home: React.FC<HomeProps> = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter states
  const [date, setDate] = useState<{ month: string; year: string }>({
    month: searchParams.get('month') ?? moment().format('MM'),
    year: searchParams.get('year') ?? moment().format('YYYY'),
  });

  const [radius, setRadius] = useState<string>(searchParams.get('radius') ?? '100');
  const [debouncedRadius, setDebouncedRadius] = useState<string>(searchParams.get('radius') ?? '100');

  const [zip, setZip] = useState<string>(() => {
    return searchParams.get('zip') ?? localStorage.getItem('zip') ?? '22066';
  });
  const [debouncedZip, setDebouncedZip] = useState<string>(() => {
    return searchParams.get('zip') ?? localStorage.getItem('zip') ?? '22066';
  });

  const [eventTypeFilters, setEventTypeFilters] = useState<string[]>(searchParams.get('events')?.split(',') ?? []);
  const [statusFilters, setStatusFilters] = useState<string[]>(searchParams.get('status')?.split(',') ?? []);
  const [entryMethodFilters, setEntryMethodFilters] = useState<string[]>(
    searchParams.get('entryMethod')?.split(',') ?? []
  );

  const filterSetters = {
    type: setEventTypeFilters,
    status: setStatusFilters,
    entryMethod: setEntryMethodFilters,
  };

  const [eventsList, setEventsList] = useState<CalendarEvent[]>([]);
  const [calendarType, setCalendarType] = useState<CalendarType>(CalendarType.Line);

  const {
    data: events,
    isLoading,
    isError,
  } = useGetEvents({
    month: date.month,
    year: date.year,
    zip: debouncedZip,
    radius: debouncedRadius,
    events: eventTypeFilters.join(','),
    status: statusFilters.join(','),
    entryMethod: entryMethodFilters.map(method => EventEntryMethods[method]).join(','),
  });

  const setDebouncedRadiusCallback = useCallback(
    debounce((value: string) => {
      setDebouncedRadius(value);
    }, 500),
    []
  );

  useEffect(() => {
    setDebouncedRadiusCallback(radius);
  }, [radius]);

  const setDebouncedZipCallback = useCallback(
    debounce((value: string) => {
      if (value && value.length === 5) {
        localStorage.setItem('zip', value);
        setDebouncedZip(value);
      }
    }, 500),
    []
  );

  useEffect(() => {
    setDebouncedZipCallback(zip);
  }, [zip]);

  useEffect(() => {
    const dedupEvents = events
      ?.reduce((unique, o) => {
        // Need to reduce as multiple events have multiple trials on the same day
        if (
          !unique.some(
            obj => obj.eventName === o.eventName && obj.startDate === o.startDate && obj.endDate === o.endDate
          )
        ) {
          unique.push(o);
        }
        return unique;
      }, [])
      .map(e => {
        return {
          title: e.eventName,
          start: moment(e.startDate, 'YYYY-MM-DD').toDate(),
          end: moment(e.endDate, 'YYYY-MM-DD').toDate(),
          resource: {
            type: EventType[e.eventType],
            data: e,
          },
        } as CalendarEvent;
      })
      .sort((a, b) => (a.resource.type > b.resource.type ? 1 : -1));

    setEventsList(dedupEvents);
  }, [events]);

  const handleUpdateFilter = (filter: 'type' | 'status' | 'entryMethod', target: string) => {
    const setFilter = filterSetters[filter];

    if (target === 'ALL') {
      setFilter([]);
    } else {
      setFilter(prev => (prev.includes(target) ? prev.filter(item => item !== target) : [...prev, target]));
    }
  };

  const clearFilters = () => {
    setRadius('100');
    setEventTypeFilters([]);
    setStatusFilters([]);
    setEntryMethodFilters([]);
  };

  // Set the URL's query params
  useEffect(() => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev);

        params.set('month', date.month);
        params.set('year', date.year);
        params.set('zip', debouncedZip);
        params.set('radius', debouncedRadius);

        if (eventTypeFilters.length) {
          params.set('events', eventTypeFilters.join(','));
        } else {
          params.delete('events');
        }

        if (statusFilters.length) {
          params.set('status', statusFilters.join(','));
        } else {
          params.delete('status');
        }

        if (entryMethodFilters.length) {
          params.set('entryMethod', entryMethodFilters.join(','));
        } else {
          params.delete('entryMethod');
        }

        return params;
      },
      { replace: true }
    );
  }, [
    date.month,
    date.year,
    debouncedZip,
    debouncedRadius,
    eventTypeFilters,
    statusFilters,
    entryMethodFilters,
    setSearchParams,
  ]);

  return (
    <>
      {isLoading && <Loader />}
      {isError && <Navigate to="/error" replace={true} />}
      <Row className="mb-2">
        <Col>
          <FilterMenu
            selectedZip={zip}
            selectedRadius={radius}
            selectedFilters={{
              types: eventTypeFilters,
              statuses: statusFilters,
              entryMethods: entryMethodFilters,
            }}
            onUpdateZip={(zip: string) => {
              setZip(zip);
            }}
            onUpdateRadius={(radius: string) => {
              setRadius(radius);
            }}
            onUpdateFilter={handleUpdateFilter}
          />
        </Col>
      </Row>
      <Row className="mb-1">
        <Col className="selected-filters-container d-flex align-items-center gap-2 flex-nowrap overflow-x-auto">
          <FilterChip
            label={'location'}
            text={
              <span className="d-flex align-items-center gap-2">
                {zip}
                <span className="opacity-25">|</span>
                {radius} mi
              </span>
            }
            disableDelete={radius === '100'}
            onDelete={() => setRadius('100')}
          />
          {eventTypeFilters.map((typeFilter, index) => (
            <FilterChip
              key={`type-${index}`}
              bg={EventTypes[typeFilter].color}
              label={'type'}
              text={EventTypes[typeFilter].name}
              onDelete={() => handleUpdateFilter('type', typeFilter)}
            />
          ))}
          {statusFilters.map((statusFilter, index) => (
            <FilterChip
              key={`status-${index}`}
              label={'status'}
              text={EventStatuses[statusFilter]}
              onDelete={() => handleUpdateFilter('status', statusFilter)}
            />
          ))}
          {entryMethodFilters.map((methodFilter, index) => (
            <FilterChip
              key={`status-${index}`}
              label={'entry method'}
              text={EventEntryMethods[methodFilter]}
              onDelete={() => handleUpdateFilter('entryMethod', methodFilter)}
            />
          ))}
        </Col>
        <Col xs="auto">
          {(radius !== '100' ||
            eventTypeFilters.length > 0 ||
            statusFilters.length > 0 ||
            entryMethodFilters.length > 0) && (
            <Button
              variant="outline-light"
              className="clear-btn p-0 rounded-circle d-flex align-items-center justify-content-center text-muted"
              onClick={() => clearFilters()}>
              <IoMdClose size={20} />
            </Button>
          )}
        </Col>
      </Row>
      <Row className="align-items-center mb-3">
        <Col>{events ? events.length : 0} events</Col>
        <Col xs={'auto'} className="my-2 d-flex gap-2 align-items-center">
          <ToggleButton
            key={CalendarType.Line}
            className="display-toggle text-body rounded-3"
            type="radio"
            variant="outline-light"
            value={CalendarType.Line}
            checked={calendarType === CalendarType.Line}
            onClick={e => {
              e.preventDefault();
              setCalendarType(CalendarType.Line);
            }}>
            <MdDensityMedium />
          </ToggleButton>
          <ToggleButton
            key={CalendarType.Compact}
            className="display-toggle text-body rounded-3"
            type="radio"
            variant="outline-light"
            value={CalendarType.Compact}
            checked={calendarType === CalendarType.Compact}
            onClick={e => {
              e.preventDefault();
              setCalendarType(CalendarType.Compact);
            }}>
            <MdFormatAlignJustify />
          </ToggleButton>
        </Col>
      </Row>
      <Row className="mb-5">
        <Col>
          <EventCalendar
            key={calendarType}
            date={moment(`${date.month} ${date.year}`, 'MM YYYY').toDate()}
            events={eventsList}
            calendarType={calendarType}
            setDate={setDate}
          />
        </Col>
      </Row>
    </>
  );
};

export default Home;
